import { CommandProps, Editor, Extension } from '@tiptap/core'
import { ResolvedPos } from '@tiptap/pm/model'

/**
 * The HTML `type` attribute of an `<ol>`.
 *
 * `null` means "no marker chosen": the list follows the positional cascade in
 * rtf/rtfListStyles.ts (decimal -> lower-alpha -> lower-roman, repeating every three levels).
 * That is what Word does with a level the author has not overridden, and what a list created
 * with Tab or by pasting always gets.
 *
 * Any other value overrides that one level only. Sublevels are separate `<ol>` nodes with no
 * attribute of their own, so they keep following the cascade — which is exactly Word's
 * "A. -> a. -> i. -> 1." in the reference screenshot.
 *
 * `'i'` is in the union because the cascade renders it at every third level, so
 * `markerForLevel` has to be able to name it. The toolbar only offers `'1' | 'a' | 'A'`.
 * A value outside the union (a pasted `type="I"`, say) is passed through untouched; the CSS
 * excludes every typed list, so the browser renders it correctly regardless.
 */
export type OrderedListMarker = null | '1' | 'a' | 'A' | 'i'

/** A marker the author picked — the values the toolbar can produce. */
export type ExplicitOrderedListMarker = Exclude<OrderedListMarker, null>

/**
 * The cascade rtf/rtfListStyles.ts renders, in the same order. Keep the two in sync: this
 * array is what tells the toolbar which menu item to tick on a list that has no `type` of
 * its own.
 */
const MARKER_CASCADE: readonly ExplicitOrderedListMarker[] = ['1', 'a', 'i']

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      orderedListType: {
         /** Set the marker type on the closest ordered list ancestor of the selection. */
         setOrderedListType: (type: OrderedListMarker) => ReturnType
      }
   }
}

export const OrderedListType = Extension.create({
   name: 'orderedListType',

   addCommands() {
      return {
         setOrderedListType:
            (type: OrderedListMarker) =>
            ({ tr, dispatch }: CommandProps) => {
               // Resolved from tr, not state, so this works as a follow-up link in a chain:
               // toggleOrderedList() has already wrapped/converted the list and moved positions.
               const { $from } = tr.selection

               // Walk up to the *closest* ordered list only. Using updateAttributes() here would
               // hit every orderedList ancestor and retype the outer list of a nested list too.
               // That narrowness is the whole feature: a chosen marker owns its own level and
               // leaves the sublevels to the cascade.
               for (let depth = $from.depth; depth > 0; depth--) {
                  const node = $from.node(depth)

                  if (node.type.name !== 'orderedList') {
                     continue
                  }

                  if (dispatch) {
                     tr.setNodeMarkup($from.before(depth), undefined, { ...node.attrs, type })
                  }

                  return true
               }

               return false
            },
      }
   },
})

/**
 * The 1-based list level of `$pos`, counted the way rtf/rtfListStyles.ts counts it: one level
 * per `listItem` ancestor, whatever kind of list wraps it. A bullet list nested between two
 * ordered lists therefore consumes a level, exactly as it does in Word and in the CSS — which
 * is why this counts list items rather than ordered lists.
 *
 * Returns 0 outside any list. Pure, so it is unit-testable without an EditorView.
 */
export const listLevel = ($pos: ResolvedPos): number => {
   let level = 0

   for (let depth = $pos.depth; depth > 0; depth--) {
      if ($pos.node(depth).type.name === 'listItem') {
         level++
      }
   }

   return level
}

/** The marker an un-attributed list renders with at `level` (1-based). */
export const markerForLevel = (level: number): OrderedListMarker =>
   level < 1 ? null : MARKER_CASCADE[(level - 1) % MARKER_CASCADE.length]

/**
 * The marker of the ordered list the selection sits in.
 * Normalises the attribute to `null` — it is `undefined` on a freshly created list but `null`
 * after a getHTML() -> setContent() round trip, since parseHTML reads a missing attribute as null.
 */
export const getOrderedListMarker = (editor: Editor): OrderedListMarker =>
   (editor.getAttributes('orderedList').type as OrderedListMarker) ?? null

/**
 * The marker the list is actually *showing*: its own attribute, or the cascade's default for
 * its level when it has none. This, not the raw attribute, is what the toolbar has to compare
 * against — otherwise picking "1. 2. 3." on an untouched level 1 list would retype it instead
 * of turning the list off, and the menu would tick nothing on a list whose marker came from
 * the cascade.
 */
export const getEffectiveOrderedListMarker = (editor: Editor): OrderedListMarker =>
   getOrderedListMarker(editor) ?? markerForLevel(listLevel(editor.state.selection.$from))

/**
 * Word-like toggle: picking the marker the list already shows turns the list off,
 * picking a different one converts that level in place.
 */
export const applyOrderedList = (editor: Editor, type: ExplicitOrderedListMarker): void => {
   if (editor.isActive('orderedList')) {
      if (getEffectiveOrderedListMarker(editor) === type) {
         editor.chain().focus().toggleOrderedList().run()
      } else {
         editor.chain().focus().setOrderedListType(type).run()
      }
      return
   }

   // toggleList() copies no attributes when converting a bullet list into an ordered list,
   // so the marker has to be applied as a second step.
   editor.chain().focus().toggleOrderedList().setOrderedListType(type).run()
}
